/**
 * Error Handling Utilities
 * Provide consistent error message extraction from Redux thunk results
 */

/**
 * Extract error message from Redux thunk result payload
 * @param {*} payload - Redux thunk result payload (can be string or object)
 * @param {string} defaultMessage - Default error message if extraction fails
 * @returns {string} Error message
 */
export const extractErrorMessage = (payload, defaultMessage = 'An error occurred. Please try again.') => {
    if (typeof payload === 'string') {
        return payload
    }
    if (payload && typeof payload === 'object' && payload.error) {
        return payload.error
    }
    return defaultMessage
}

/**
 * Check if error message indicates email verification is required
 * @param {string} errorMessage - Error message to check
 * @returns {boolean} True if email verification is required
 */
export const isEmailVerificationError = errorMessage => {
    if (!errorMessage || typeof errorMessage !== 'string') return false
    return (
        errorMessage.includes('verify your email') ||
        errorMessage.includes('Please verify') ||
        errorMessage.toLowerCase().includes('email not verified')
    )
}

/**
 * Check if error message indicates email already exists
 * @param {string} errorMessage - Error message to check
 * @returns {boolean} True if email already exists
 */
export const isEmailExistsError = errorMessage => {
    if (!errorMessage || typeof errorMessage !== 'string') return false
    return (
        errorMessage.includes('already exists') ||
        errorMessage.includes('Email already') ||
        errorMessage.toLowerCase().includes('email is already registered')
    )
}

/**
 * Check if error message indicates token is invalid or expired
 * @param {string} errorMessage - Error message to check
 * @returns {boolean} True if token is invalid or expired
 */
export const isTokenInvalidError = errorMessage => {
    if (!errorMessage || typeof errorMessage !== 'string') return false
    return (
        errorMessage.includes('Invalid password reset token') ||
        errorMessage.includes('Password reset token has expired') ||
        errorMessage.includes('No password reset token found') ||
        errorMessage.includes('Invalid or expired token')
    )
}

/**
 * Check if error message indicates rate limit exceeded
 * @param {string} errorMessage - Error message to check
 * @returns {boolean} True if rate limit is exceeded
 */
export const isRateLimitError = errorMessage => {
    if (!errorMessage || typeof errorMessage !== 'string') return false
    return errorMessage.includes('Too many authentication requests')
}
